import React, { MouseEvent } from 'react';
import { WarningCircleIcon } from '@fronton/icons-react';
import cn from 'clsx';
import { useStoreon } from 'storeon/react';
import { Button, CashbackPlate, SectionSubTitle, Typography } from 'common/components';
import { COLLECTIONS_STORE_NAME, selectCollectionInfo, VariantAxisPoint } from 'modules/collections';
import { RELATED_SERVICES_STORE_NAME, RelatedService, selectActiveServices } from 'modules/related-services';
import { State } from 'store/types';
import { createElementKey } from 'utils';
import { AxisPoints } from '../axis-points';
import { ProductPrice } from '../product-price';
import { ProductServices } from '../product-services';
import {
  productInfo,
  productInfoBasketBtnWrapper,
  productOnfoCashbackWrapper,
  productInfoDisclaimer,
  productInfoName,
  productInfoNameWithoutAxisPoint,
  productInfoNote,
} from './product-info-styles';

type ProductInfoProps = {
  areAllSizesUnavailable?: boolean;
  basketHref: string;
  buttonLabel: string;
  cashback?: number;
  discount?: number;
  disclaimer: string;
  infoNote: string[];
  onAddToCartClick: () => void;
  onAxisPointClick?: (point: VariantAxisPoint) => void;
  onServiceClick: (service: RelatedService, position: number) => void;
  onServiceShowEvent?: (service: RelatedService, position: number) => void;
  pricePrefix?: string;
  serviceDisclaimer?: React.ReactNode;
  servicePrefixPrice?: string;
  basketHrefTarget?: '_blank' | '_self';
  isNewKitchen?: boolean;
};

export const ProductInfo: React.FC<ProductInfoProps> = ({
  areAllSizesUnavailable,
  basketHref,
  buttonLabel,
  disclaimer,
  infoNote,
  onAxisPointClick,
  onAddToCartClick,
  onServiceClick,
  onServiceShowEvent,
  pricePrefix,
  serviceDisclaimer,
  servicePrefixPrice,
  basketHrefTarget,
  isNewKitchen = false,
}) => {
  const { collections, relatedServices } = useStoreon<State>(
    COLLECTIONS_STORE_NAME,
    RELATED_SERVICES_STORE_NAME
  );
  const collectionInfo = selectCollectionInfo(collections);
  const activeServices = selectActiveServices(relatedServices);
  const { variantsModel } = collectionInfo.variants;

  const handleAxisPointClick = (axisId: string, productId: string) => () => {
    const clickedPoint = variantsModel
      .find(variant => variant.axisMeta.attribute === axisId)
      ?.pointsOnAxis.find(point => point.productId === productId);

    if (!clickedPoint) {
      return;
    }

    if (onAxisPointClick) onAxisPointClick(clickedPoint);
  };

  const handleAddToCartWheelClick = (e: MouseEvent) => {
    if (e.button === 1) {
      onAddToCartClick();
    }
  };

  return (
    <div className={productInfo}>
      <SectionSubTitle
        as="h1"
        className={cn(productInfoName, {
          [productInfoNameWithoutAxisPoint]: collectionInfo.variants.variantsModel.length === 0,
        })}
        color={areAllSizesUnavailable ? 'text-minor' : 'text-primary'}
        itemProp="name"
        margin="m">
        {collectionInfo.variants.activePoint.displayedName}
      </SectionSubTitle>

      {collectionInfo.variants.activePoint.price.cashback && (
        <CashbackPlate
          cashback={collectionInfo.variants.activePoint.price.cashback}
          className={productOnfoCashbackWrapper}
          size="l"
        />
      )}

      {collectionInfo.variants.variantsModel.length > 0 && !areAllSizesUnavailable && (
        <AxisPoints
          activeProductId={collectionInfo.variants.activePoint.productId}
          axisPoints={collectionInfo.variants.variantsModel}
          onPointClick={handleAxisPointClick}
        />
      )}

      {(areAllSizesUnavailable || !collectionInfo.variants.activePoint.available) && (
        <Typography variant="l" size="body_accent">
          Товар закончился
        </Typography>
      )}

      {!areAllSizesUnavailable && collectionInfo.variants.activePoint.available && (
        <>
          <ProductPrice
            discount={collectionInfo.variants.activePoint.price.discount}
            isNewKitchen={isNewKitchen}
            price={collectionInfo.variants.activePoint.price.price}
            prevPrice={collectionInfo.variants.activePoint.price.prevPrice}
            prefix={pricePrefix}
          />
          <div className={productInfoDisclaimer}>
            <WarningCircleIcon type="fill" size="m" color="var(--secondary-orange_primary)" />
            <Typography color="secondary-orange-primary" variant="s" size="body_long">
              {disclaimer}
            </Typography>
          </div>

          <div className={productInfoBasketBtnWrapper}>
            <Button
              href={basketHref}
              target={basketHrefTarget || '_self'}
              onClick={onAddToCartClick}
              onAuxClick={handleAddToCartWheelClick}
              variant="primary"
              wide>
              {buttonLabel}
            </Button>
          </div>

          <div className={productInfoNote}>
            {infoNote.map((note, index) => (
              <Typography
                key={createElementKey('product-info-note', index)}
                variant="minor_caption"
                color="text-minor">
                {note}
              </Typography>
            ))}
          </div>

          {activeServices && activeServices.length > 0 && (
            <ProductServices
              activeServices={activeServices}
              onServiceClick={onServiceClick}
              onServiceShowEvent={onServiceShowEvent}
              prefixPrice={servicePrefixPrice}
              serviceDisclaimer={serviceDisclaimer}
            />
          )}
        </>
      )}
    </div>
  );
};
